import { ComponentType } from '@angular/cdk/portal'
import { Injectable } from '@angular/core'
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog'
import { DialogData } from 'libs/shared/ui/src/lib/gb-confirm-dialog/gb-confirm-dialog-data'
import { GbConfirmDialogComponent } from 'libs/shared/ui/src/lib/gb-confirm-dialog/gb-confirm-dialog.component'
import { filter, map, Observable, of } from 'rxjs'

/**
 * GbBaseDialogService which handles the opening and return value of dialogs
 *
 * To use in effects, return the 'getDialogObs' function via an action within a higher order
 * mapping operator (switchMap, mergeMap etc.).
 * This will mean the return value of the dialog is added into the effect stream.
 *
 * To close the dialog in effects, call closeDialog via an action, passing in the return value.
 *
 * This service should not be accessed directly by services and component, rather, import the
 * GbFrontDialogService or GbDashboardDialogService for the front or dashboard respectively
 */

export interface GbDialogConfig extends Omit<MatDialogConfig, 'maxHeight'> {
  mobileFullScreen?: boolean
}

export interface GbDialogResult {
  [key: string]: any
}

@Injectable({
  providedIn: 'root',
})
export class GbBaseDialogService {
  /**
   * Default dialog config
   */

  public defaultDialogConfig: GbDialogConfig = {
    mobileFullScreen: true,
    autoFocus: false,
    disableClose: false,
  }

  /**
   * Dialog ref map to store the MatDialogRef
   */

  dialogRefMap = new Map<ComponentType<any>, MatDialogRef<any>>()

  constructor(protected matDialog: MatDialog) {}

  public get afterAllClosed$(): Observable<void> {
    return this.matDialog.afterAllClosed
  }

  /**
   * Opens dialog
   * @template T
   * @param component
   * @param [dialogConfig]
   */

  public openDialog<T>(component: ComponentType<T>, dialogConfig?: GbDialogConfig): MatDialogRef<any> {
    // Close any existing dialog of the same component type before opening a new one
    const existingDialogRef = this.dialogRefMap.get(component)
    if (existingDialogRef) {
      existingDialogRef.close()
      this.dialogRefMap.delete(component)
    }
    const config = this.getConfig(dialogConfig)

    const dialogRef = this.matDialog.open(component, config)
    this.dialogRefMap.set(component, dialogRef)
    return dialogRef
  }

  /**
   * Closes dialog
   * @template T
   * @param component
   * @param [dialogResult]
   */

  public closeDialog<T>(component: ComponentType<T>, dialogResult?: string | boolean | GbDialogResult): void {
    const dialogRef: MatDialogRef<T> | undefined = this.dialogRefMap.get(component)

    if (dialogRef) {
      dialogRef.close(dialogResult)
      this.dialogRefMap.delete(component)
      return
    }
  }

  /**
   * Open a dialog and return it's result as an observable
   * @param component
   * @param [dialogConfig]
   * @returns dialog obs
   *
   * This function opens a dialog window using the specified component and dialog configuration,
   * and returns an Observable that emits the result of the dialog after it is closed.
   */

  public getDialogObs(
    component: ComponentType<any>,
    dialogConfig?: GbDialogConfig,
  ): Observable<string | boolean | GbDialogResult> {
    this.openDialog(component, dialogConfig)

    const dialogRef = this.dialogRefMap.get(component)

    if (dialogRef) return dialogRef?.afterClosed()
    return of(false)
  }

  /**
   * Gets config
   * @param dialogConfig
   * @returns config mapped to the MatDialogConfig
   */

  public getConfig(dialogConfig: GbDialogConfig = this.defaultDialogConfig): MatDialogConfig {
    const panelClasses: string[] = []

    if (!dialogConfig.panelClass) {
      dialogConfig.panelClass = []
    }

    if (typeof dialogConfig.panelClass === 'string') {
      dialogConfig.panelClass = [dialogConfig.panelClass]
    }

    panelClasses.push(...dialogConfig.panelClass)

    // undefined must be mobile fullScreen too
    if (dialogConfig.mobileFullScreen !== false) {
      panelClasses.push('mobile-dialog-full-screen')
    }

    let classesConfig = {
      panelClass: panelClasses,
    }

    let config: MatDialogConfig = {
      ...this.defaultDialogConfig,
      ...dialogConfig,
      ...classesConfig,
      // Apply constant maxHeight that cannot be overridden
      maxHeight: '90vh',
    }

    return config
  }

  openConfirmDialog(dialogData: DialogData, dialogConfig?: GbDialogConfig) {
    return this.getDialogObs(GbConfirmDialogComponent, {
      data: dialogData,
      maxWidth: '400px',
      width: '400px',
      disableClose: !!dialogData.disableClose,
      ...dialogConfig,
    })
  }

  /**
   * Opens a confirmation dialog and emits the provided action
   * only on confirm (i.e. truthy result)
   */
  filterByConfirmDialog<T>(dialogData: DialogData, action: T): Observable<T> {
    return this.openConfirmDialog(dialogData).pipe(
      filter((result) => !!result),
      map(() => action),
    )
  }
}
